import { isSlugDomain } from "@/utils/domain-utils";

const SITE_SEGMENTS = new Set([
  "eventos",
  "grupos",
  "ensino",
  "comunidade",
  "artigos",
  "blog",
  "biblia",
  "cadastro-membros",
]);

/** Rotas públicas do Site da Igreja (shell + filhas). */
const NON_SITE_FIRST_SEGMENTS = new Set([
  "login",
  "links",
  "lp",
  "campanha",
  "sobre",
  "app",
  "download",
  "pedidos-oracao",
  "pagamento",
  "ecommerce",
  "sistema",
  "mercadopago",
  "checkin",
  "inscricao",
  "cadastros",
  "administracao",
  "novo-usuario",
  "recuperar-senha",
  "nova-senha",
  "privacidade",
  "termos",
]);

/** Normaliza path para comparação (decode URI + trim de barras finais). */
export function normalizeSitePath(pathname: string): string {
  if (!pathname) return "/";

  let decoded = pathname;
  try {
    let prev = "";
    while (decoded !== prev) {
      prev = decoded;
      decoded = decodeURIComponent(decoded.replace(/\+/g, " "));
    }
  } catch {
    decoded = pathname;
  }

  const trimmed = decoded.replace(/\/+$/, "");
  return trimmed || "/";
}

function splitSitePath(pathname: string): string[] {
  const normalized = normalizeSitePath(pathname);
  if (normalized === "/") return [];
  return normalized.slice(1).split("/").filter(Boolean);
}

export function isSiteIgrejaPublicPath(pathname: string): boolean {
  const segments = splitSitePath(pathname);

  if (segments.length === 0) return true;

  if (SITE_SEGMENTS.has(segments[0]) || segments[0] === "comunidade" || segments[0] === "artigos") {
    return true;
  }

  if (segments.length >= 2) {
    if (SITE_SEGMENTS.has(segments[1]) || segments[1] === "comunidade" || segments[1] === "artigos") {
      return true;
    }
  }

  // Dev: /:login (slug da organização no path)
  if (segments.length === 1 && !NON_SITE_FIRST_SEGMENTS.has(segments[0])) {
    return true;
  }

  return false;
}

/** Home pública do site (hero com nav sobreposto). */
export function isSiteIgrejaHomePath(pathname: string, basePath = ""): boolean {
  const path = normalizeSitePath(pathname);
  const base = normalizeSitePath(basePath || "/");
  if (base && base !== "/") return path === base;
  return path === "/";
}

/** Raiz do domínio principal sem org na URL: cai na landing comercial do SysIgreja. */
export function isSysIgrejaLandingEntryPath(pathname: string): boolean {
  return !isSlugDomain() && normalizeSitePath(pathname) === "/";
}

/** Path relativo ao basePath do site (ex.: "eventos", "comunidade/artigo"). */
export function getSiteIgrejaRelativePath(pathname: string, basePath: string): string {
  const path = normalizeSitePath(pathname);
  const base = normalizeSitePath(basePath || "/");

  if (base && base !== "/") {
    if (path === base) return "";
    if (path.startsWith(`${base}/`)) {
      return path.slice(base.length + 1);
    }
  }

  if (path === "/") return "";
  return path.slice(1);
}

/** Item do nav lateral/topo — destaca rota atual e subrotas (ex.: /comunidade/artigo). */
export function isSiteNavItemActive(
  pathname: string,
  basePath: string,
  segment: string,
): boolean {
  if (!segment) {
    return isSiteIgrejaHomePath(pathname, basePath);
  }

  const path = normalizeSitePath(pathname);
  const base = normalizeSitePath(basePath || "/");
  const itemPath = base && base !== "/" ? `${base}/${segment}` : `/${segment}`;

  return path === itemPath || path.startsWith(`${itemPath}/`);
}
